import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/components/finance/financeUtils";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle2, Clock3, CreditCard, Download, ExternalLink, FileText, Loader2, ReceiptText, ShieldAlert, XCircle } from "lucide-react";
import { toast } from "sonner";

// Every figure on these cards comes from the server's records (finance or
// reporting); the browser never computes or sends amounts or counts.

export const REASON_TEXT = {
  no_current_term: "Ordering is paused until the current term is set.",
  not_eligible_for_class: "Not offered for this child's class.",
  choose_programme: "Choose the programme this is for.",
  unpaid_order_exists: "An earlier order is still unpaid.",
  term_limit_reached: "Term limit reached.",
};

const STATE = {
  confirmed: { label: "Confirmed", tone: "text-emerald-700 bg-emerald-50", Icon: CheckCircle2 },
  awaiting_payment: { label: "Awaiting payment", tone: "text-amber-700 bg-amber-50", Icon: Clock3 },
  processing: { label: "Processing", tone: "text-amber-700 bg-amber-50", Icon: Clock3 },
  initializing: { label: "Starting", tone: "text-amber-700 bg-amber-50", Icon: Clock3 },
  expired: { label: "Expired", tone: "text-slate-600 bg-slate-100", Icon: XCircle },
  failed: { label: "Failed", tone: "text-red-700 bg-red-50", Icon: XCircle },
  needs_review: { label: "Under review", tone: "text-red-700 bg-red-50", Icon: XCircle },
};

export const formatDate = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" }) : "";
};

export const startCheckout = (link) => {
  sessionStorage.setItem("dfa_paystack_pending", JSON.stringify({ reference: link.reference, invoice_ids: link.invoice_ids || [], combined: true }));
  window.location.assign(link.authorization_url);
};

const Shell = ({ title, icon, children, testId }) => <div className="mt-2 rounded-xl border border-[#1B2C5A]/10 bg-white p-4 shadow-sm" data-testid={testId}>
  {title && <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-[#1B2C5A]/70 font-medium mb-3">{icon}{title}</div>}
  {children}
</div>;

const Row = ({ label, value, strong }) => <div className="flex justify-between gap-3 py-1 text-sm">
  <span className="min-w-0 break-words text-[#475569]">{label}</span><span className={"shrink-0 whitespace-nowrap text-right " + (strong ? "font-semibold text-[#1B2C5A]" : "text-[#1B2C5A]")}>{value}</span>
</div>;

const StateBadge = ({ state }) => {
  const meta = STATE[state] || STATE.failed;
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${meta.tone}`}><meta.Icon className="w-3 h-3" />{meta.label}</span>;
};

const BalanceSummary = ({ block, onQuick, busy }) => {
  const allIds = block.students.flatMap((student) => student.periods.flatMap((period) => period.items.map((item) => item.invoice_id)));
  if (!block.students.length) return null;
  return <Shell title={block.scope?.type === "current_term" ? `Outstanding · ${block.scope.term_name || "This term"}` : "Outstanding bills"} testId="assistant-balance">
    {block.students.map((student) => <div key={student.student_id} className="mb-3 last:mb-0">
      <div className="font-display text-[#1B2C5A]">{student.name}</div>
      {student.periods.map((period) => <div key={`${period.session_name}-${period.term_name}`} className="mt-1 pl-3 border-l-2 border-[#9DCD3F]/50">
        <div className="text-xs text-[#475569]">{[period.term_name, period.session_name].filter(Boolean).join(" · ")}</div>
        {period.items.map((item) => <div key={item.invoice_id} className="flex justify-between gap-3 text-sm py-0.5">
          <span className="min-w-0 break-words">{item.title}{item.payment_in_progress && <span className="ml-2 text-[10px] uppercase text-amber-700">payment open</span>}</span>
          <span className="shrink-0 whitespace-nowrap font-medium">{money(item.outstanding_minor)}</span>
        </div>)}
      </div>)}
      {block.students.length > 1 && <Row label={`${student.name.split(" ")[0]} total`} value={money(student.total_minor)} />}
    </div>)}
    <div className="border-t mt-2 pt-2"><Row label="Total outstanding" value={money(block.total_outstanding_minor)} strong /></div>
    {block.available_credit_minor > 0 && <p className="text-xs text-[#475569] mt-1">Unallocated credit on account: {money(block.available_credit_minor)}. The bursary applies it to bills.</p>}
    <div className="flex flex-wrap gap-2 mt-3">
      <Button size="sm" disabled={busy} onClick={() => onQuick({ type: "prepare_invoice_payment", invoice_ids: allIds })} data-testid="assistant-pay-all"><CreditCard className="w-4 h-4 mr-1" />Pay {block.students.length > 1 || allIds.length > 1 ? "all" : "now"}</Button>
      {block.students.length > 1 && block.students.map((student) => <Button key={student.student_id} size="sm" variant="outline" disabled={busy}
        onClick={() => onQuick({ type: "prepare_invoice_payment", invoice_ids: student.periods.flatMap((period) => period.items.map((item) => item.invoice_id)) })}>
        Pay for {student.name.split(" ")[0]}</Button>)}
    </div>
  </Shell>;
};

const ChildOrder = ({ item, child, onQuick, busy }) => {
  const [quantity, setQuantity] = useState(1);
  const order = (extra = {}) => onQuick({ type: "prepare_order", product_id: item.product_id, student_id: child.student_id, quantity, ...extra });
  return <div className="flex flex-wrap items-center justify-between gap-2 py-1.5 text-sm">
    <span className="text-[#1B2C5A]">{child.student_name}</span>
    {child.eligible ? <span className="flex items-center gap-2">
      {child.max_quantity > 1 && <select aria-label="Quantity" className="border rounded-md px-2 py-1 text-sm" value={quantity} onChange={(event) => setQuantity(Number(event.target.value))}>
        {Array.from({ length: child.max_quantity }, (_, index) => index + 1).map((value) => <option key={value} value={value}>{value}</option>)}
      </select>}
      <Button size="sm" disabled={busy} onClick={() => order()}>Order</Button>
    </span> : child.reason === "choose_programme" ? <span className="flex flex-wrap gap-1">
      {(child.placements || []).map((placement) => <Button key={placement.enrollment_id} size="sm" variant="outline" disabled={busy} onClick={() => order({ enrollment_id: placement.enrollment_id })}>{placement.program_name}</Button>)}
    </span> : child.reason === "unpaid_order_exists" ? <Button size="sm" variant="outline" disabled={busy} onClick={() => onQuick({ type: "prepare_invoice_payment", invoice_ids: child.unpaid_invoice_ids })}>
      Pay unpaid order ({money(child.unpaid_amount_minor)})</Button> : <span className="text-xs text-[#475569]">{REASON_TEXT[child.reason] || "Not available"}</span>}
  </div>;
};

const ProductOptions = ({ block, onQuick, busy }) => <Shell title="Approved items" testId="assistant-products">
  {block.items.map((item) => <div key={item.product_id} className="mb-3 last:mb-0 border-b last:border-0 pb-2 last:pb-0">
    <div className="flex justify-between gap-3"><span className="font-display text-[#1B2C5A]">{item.name}</span><span className="font-semibold">{money(item.unit_price_minor)}<span className="text-xs font-normal text-[#475569]"> / {item.unit || "unit"}</span></span></div>
    {item.children.map((child) => <ChildOrder key={child.student_id} item={item} child={child} onQuick={onQuick} busy={busy} />)}
  </div>)}
</Shell>;

const PaymentConfirmation = ({ block, onConfirm, onCancel, busy, resolved }) => <Shell title={block.kind === "product_order" ? block.item_name : "Bill payment"} icon={<CreditCard className="w-3.5 h-3.5" />} testId="assistant-confirmation">
  {block.kind === "product_order" ? <>
    <Row label="Student" value={[block.student_name, block.class_name].filter(Boolean).join(" · ")} />
    <Row label="Term" value={block.term_name} />
    <Row label="Quantity" value={block.quantity} />
    {block.quantity > 1 && <Row label="Unit price" value={money(block.unit_price_minor)} />}
  </> : block.lines.map((line) => <Row key={line.invoice_id} label={`${line.title} · ${line.student_name.split(" ")[0]}`} value={money(line.amount_minor)} />)}
  <div className="border-t mt-2 pt-2"><Row label="Amount" value={money(block.total_minor)} strong /></div>
  {resolved ? <p className="text-xs text-[#475569] mt-3">{resolved}</p> : <>
    <p className="text-xs text-[#475569] mt-2">Nothing is charged until you press Generate Payment.</p>
    <div className="flex gap-2 mt-3 justify-end">
      <Button size="sm" variant="outline" disabled={busy} onClick={() => onCancel(block.action_id)}>Cancel</Button>
      <Button size="sm" disabled={busy} onClick={() => onConfirm(block.action_id)} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="assistant-generate-payment">
        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Generate Payment</Button>
    </div>
  </>}
</Shell>;

const PaymentLink = ({ block }) => {
  const expired = block.expires_at && new Date(block.expires_at) < new Date();
  return <Shell title={block.reused ? "Open payment" : "Payment request created"} icon={<CheckCircle2 className="w-3.5 h-3.5 text-[#9DCD3F]" />} testId="assistant-payment-link">
    <Row label="Amount" value={money(block.amount_minor)} strong />
    <Row label="Status" value={expired ? "Link expired" : "Pending"} />
    <Row label="Reference" value={<span className="text-xs font-mono break-all">{block.reference}</span>} />
    {expired ? <p className="text-xs text-[#475569] mt-2">This link has expired. Ask me again to create a fresh one.</p>
      : <Button className="w-full mt-3 bg-[#9DCD3F] hover:bg-[#8BBB2F] text-[#1B2C5A] font-semibold" onClick={() => startCheckout(block)} data-testid="assistant-pay-now">PAY NOW</Button>}
  </Shell>;
};

const PaymentStatus = ({ block, onQuick, busy }) => <Shell title="Payment status" testId="assistant-status">
  {block.checkouts.map((row) => <div key={row.reference} className="py-2 border-b last:border-0">
    <div className="flex justify-between gap-2 items-center"><span className="font-semibold text-[#1B2C5A]">{money(row.amount_minor)}</span><StateBadge state={row.state} /></div>
    <div className="text-xs text-[#475569]">{row.items.map((item) => [item.title, item.student_name?.split(" ")[0]].filter(Boolean).join(" · ")).join(", ")}</div>
    <div className="text-[11px] text-[#475569]/80 font-mono break-all">{row.reference} · {formatDate(row.verified_at || row.created_at)}</div>
    {row.state === "awaiting_payment" && <p className="text-xs text-amber-700 mt-1">Not yet confirmed by the payment gateway. If you have just paid, it can take a few minutes.</p>}
    {row.state === "confirmed" && <Button size="sm" variant="outline" className="mt-2" disabled={busy} onClick={() => onQuick({ type: "receipt", reference: row.reference })}><ReceiptText className="w-4 h-4 mr-1" />View receipt</Button>}
  </div>)}
</Shell>;

const PaymentHistory = ({ block, onQuick, busy }) => <Shell title="Recent payments" testId="assistant-history">
  {block.payments.map((row) => <div key={row.payment_id} className="py-2 border-b last:border-0 flex justify-between gap-3 items-start">
    <div className="min-w-0">
      <div className="text-sm text-[#1B2C5A]">{row.student_name} · {formatDate(row.received_at)}</div>
      <div className="text-xs text-[#475569]">{row.items.map((item) => item.title).join(", ") || "Payment on account"}</div>
    </div>
    <div className="text-right shrink-0"><div className="font-semibold">{money(row.amount_minor)}</div>
      <button className="text-xs text-[#1B2C5A] underline" disabled={busy} onClick={() => onQuick({ type: "receipt", payment_id: row.payment_id })}>Receipt</button></div>
  </div>)}
</Shell>;

const Receipt = ({ block }) => {
  const [downloading, setDownloading] = useState("");
  const download = async (row) => {
    setDownloading(row.payment_id);
    try {
      const { data } = await api.get(row.pdf_path, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a"); link.href = url; link.download = `DFA-receipt-${row.reference}.pdf`; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (error) { toast.error(formatApiError(error)); } finally { setDownloading(""); }
  };
  return block.receipts.map((row) => <Shell key={row.payment_id} title="Official receipt" icon={<ReceiptText className="w-3.5 h-3.5" />} testId="assistant-receipt">
    <Row label="Student" value={row.student_name} />
    <Row label="Date" value={formatDate(row.received_at)} />
    {row.items.map((item, index) => <Row key={index} label={item.title} value={money(item.amount_minor)} />)}
    <div className="border-t mt-2 pt-2"><Row label="Amount paid" value={money(row.amount_minor)} strong /></div>
    <div className="text-[11px] text-[#475569]/80 font-mono break-all mt-1">{row.reference}</div>
    <Button size="sm" variant="outline" className="mt-3" disabled={downloading === row.payment_id} onClick={() => download(row)}>
      {downloading === row.payment_id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}Download PDF</Button>
  </Shell>);
};

const StudentSelector = ({ block, onSend, busy }) => <Shell testId="assistant-student-selector">
  <div className="text-sm text-[#1B2C5A] mb-2">{block.question}</div>
  <div className="flex flex-wrap gap-2">{block.children.map((child) => <Button key={child.student_id} size="sm" variant="outline" disabled={busy} onClick={() => onSend(`It's for ${child.name}.`)}>{child.name}</Button>)}</div>
</Shell>;

const Capabilities = ({ block }) => <Shell title="What I can help with" testId="assistant-capabilities">
  {block.available.length
    ? <ul className="list-disc pl-5 space-y-1 text-sm text-[#1B2C5A]">{block.available.map((line) => <li key={line}>{line}</li>)}</ul>
    : <p className="text-sm text-[#475569]">I can't look up school records for your account yet.</p>}
  {block.not_yet_available.length > 0 && <>
    <div className="mt-3 mb-1 text-xs text-[#475569]">Not yet available in the assistant</div>
    <ul className="list-disc pl-5 space-y-0.5 text-xs text-[#475569]">{block.not_yet_available.map((line) => <li key={line}>{line}</li>)}</ul>
  </>}
</Shell>;

// ------------------------------------------------------------------ reporting

const humanize = (value = "") => String(value).replaceAll("_", " ").toLowerCase().replace(/^./, (c) => c.toUpperCase());

// Dates arrive as school-local YYYY-MM-DD; format without shifting the day.
export const formatDay = (value) => {
  const [year, month, day] = String(value || "").split("-").map(Number);
  return year && month && day ? new Date(year, month - 1, day).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" }) : "";
};

const PRIORITY_TONE = { CRITICAL: "bg-red-50 text-red-700", HIGH: "bg-amber-50 text-amber-800", NORMAL: "bg-slate-100 text-slate-700", LOW: "bg-slate-50 text-slate-500" };
const Badge = ({ children, tone = "bg-slate-100 text-slate-700" }) => <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${tone}`}>{children}</span>;
const restricted = (report) => report.confidentiality === "RESTRICTED" || report.confidentiality === "SENSITIVE";

const OpenReport = ({ reportId, compact }) => <Link to={`/app/reports/${encodeURIComponent(reportId)}`}
  className={`inline-flex items-center gap-1 rounded-md border border-[#1B2C5A]/20 bg-white font-medium text-[#1B2C5A] hover:border-[#9DCD3F] ${compact ? "px-2 py-1 text-xs" : "px-3 py-1.5 text-sm"}`}
  data-testid="assistant-open-report"><ExternalLink className="w-3.5 h-3.5" />Open Report</Link>;

const Stat = ({ label, value, tone }) => <div className="rounded-lg border border-[#1B2C5A]/10 bg-[#FDFBF7] px-3 py-2 min-w-0">
  <div className={`text-lg font-semibold leading-tight ${tone || "text-[#1B2C5A]"}`}>{value ?? 0}</div>
  <div className="text-[11px] text-[#475569] break-words">{label}</div>
</div>;

const Breakdown = ({ title, counts, format = humanize }) => {
  const entries = Object.entries(counts || {});
  return entries.length ? <div className="min-w-0"><div className="text-[11px] uppercase tracking-wide text-[#475569] mb-1">{title}</div>
    <div className="flex flex-wrap gap-1">{entries.map(([key, count]) => <Badge key={key} tone={PRIORITY_TONE[key]}>{format(key)} · {count}</Badge>)}</div>
  </div> : null;
};

const signed = (value) => (value > 0 ? `+${value}` : `${value}`);

// The card's filters, for its PDF; the server re-runs the summary itself.
export const reportPdfFilters = (block) => {
  const period = block.period || {};
  const filters = { period: period.name || "today", compare_previous: Boolean(block.comparison) };
  if (period.name === "custom") Object.assign(filters, { start_date: period.start_date, end_date: period.end_date });
  if (block.filters?.category) filters.category = block.filters.category;
  if (block.filters?.priority) filters.priority = block.filters.priority;
  return filters;
};

const ReportSummary = ({ block, onQuick, busy }) => {
  const activity = block.activity || {};
  const open = block.open_now || {};
  const change = block.comparison?.change;
  // Older saved conversations carry the previous key names.
  const fromPeriod = block.attention?.awaiting_my_action_from_period ?? activity.awaiting_my_action_from_period ?? activity.needs_my_attention ?? 0;
  const total = block.attention?.awaiting_my_action_total ?? open.awaiting_my_action_total ?? open.needs_my_attention ?? 0;
  return <Shell title={`Report activity · ${block.period?.label || ""}`} icon={<FileText className="w-3.5 h-3.5" />} testId="assistant-report-summary">
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
      <Stat label="Submitted or updated" value={activity.reports_with_activity} />
      <Stat label="Newly submitted" value={activity.newly_submitted} />
      <Stat label="Overdue" value={activity.overdue} tone={activity.overdue ? "text-red-700" : undefined} />
      <Stat label="Awaiting my action (this period)" value={fromPeriod} tone={fromPeriod ? "text-amber-800" : undefined} />
      <Stat label="Total currently awaiting my action" value={total} tone={total ? "text-amber-800" : undefined} />
    </div>
    {block.attention?.note && <p className="mt-2 text-xs text-amber-800" data-testid="assistant-report-attention-note">{block.attention.note}</p>}
    {activity.reports_with_activity > 0 && <div className="mt-3 grid gap-3 sm:grid-cols-3">
      <Breakdown title="Priority" counts={activity.by_priority} />
      <Breakdown title="Category" counts={activity.by_category} format={(key) => key} />
      <Breakdown title="Status" counts={activity.by_status} />
    </div>}
    {block.comparison && <div className="mt-3 rounded-lg bg-[#EEF3E5]/60 px-3 py-2 text-sm text-[#1B2C5A]" data-testid="assistant-report-comparison">
      Compared with {block.comparison.previous_period?.label}: {block.comparison.previous?.reports_with_activity ?? 0} reports
      ({signed(change?.reports_with_activity ?? 0)}), newly submitted {signed(change?.newly_submitted ?? 0)}, overdue {signed(change?.overdue ?? 0)}.
    </div>}
    <div className="mt-3 border-t pt-2 text-xs text-[#475569]">Open now: {open.open ?? 0} · overdue {open.overdue ?? 0} · escalated {open.escalated ?? 0} · critical {open.critical ?? 0}</div>
    {block.truncated && <p className="mt-1 text-xs text-amber-800">Only the most recent records were counted; narrow the period for exact figures.</p>}
    <div className="flex flex-wrap gap-2 mt-3">
      {total > 0 && <Button size="sm" disabled={busy} onClick={() => onQuick({ type: "reports_attention" })}>Reports needing my attention</Button>}
      {block.period?.name && <Button size="sm" variant="outline" disabled={busy} data-testid="assistant-report-pdf"
        onClick={() => onQuick({ type: "report_summary_pdf", report_filters: reportPdfFilters(block) }, "Download report summary as PDF")}>
        <Download className="w-4 h-4 mr-1" />Download PDF</Button>}
      <Link to="/app/reports" className="inline-flex items-center gap-1 rounded-md border border-[#1B2C5A]/20 bg-white px-3 py-1.5 text-sm font-medium text-[#1B2C5A] hover:border-[#9DCD3F]">
        <ExternalLink className="w-3.5 h-3.5" />Open Reports</Link>
    </div>
  </Shell>;
};

const ReportTitle = ({ report }) => restricted(report)
  ? <span className="inline-flex items-center gap-1 text-[#1B2C5A]"><ShieldAlert className="w-4 h-4 text-red-700" />{humanize(report.confidentiality)} report</span>
  : <span className="text-[#1B2C5A]">{report.title || "Untitled report"}</span>;

const ReportList = ({ block, onQuick, busy }) => {
  const reports = block.reports || [];
  return <Shell title={`Reports${block.period?.label ? ` · ${block.period.label}` : ""} · ${reports.length} of ${block.total_matching}`} icon={<FileText className="w-3.5 h-3.5" />} testId="assistant-report-list">
    {!reports.length && <p className="text-sm text-[#475569]">No reports match.</p>}
    {reports.map((report) => <div key={report.report_id} className="py-2 border-b last:border-0" data-testid="assistant-report-row">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1 font-medium break-words"><ReportTitle report={report} /></div>
        <div className="flex flex-wrap gap-1"><Badge tone={PRIORITY_TONE[report.priority]}>{humanize(report.priority)}</Badge><Badge>{humanize(report.status)}</Badge>
          {report.overdue && <Badge tone="bg-red-50 text-red-700"><AlertTriangle className="w-3 h-3" />Overdue</Badge>}
          {report.needs_my_attention && <Badge tone="bg-amber-50 text-amber-800">Needs my action</Badge>}</div>
      </div>
      <div className="mt-1 text-xs text-[#475569] break-words">
        <span className="font-mono">{report.ref}</span> · {report.category} · submitted {formatDay(report.submitted_date)}
        {report.reporter && <> · by {report.reporter}{report.reporter_position ? ` (${report.reporter_position})` : ""}</>}
        {report.responsible_position && <> · with {report.responsible_position}{report.responsible ? ` (${report.responsible})` : ""}</>}
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={busy} onClick={() => onQuick({ type: "report_detail", report_id: report.report_id }, `Show report ${report.ref}`)}>Details</Button>
        <OpenReport reportId={report.report_id} compact />
      </div>
    </div>)}
    {block.total_matching > reports.length && <p className="mt-2 text-xs text-[#475569]">Showing {reports.length} of {block.total_matching}. Open Reports in DFA to see them all.</p>}
  </Shell>;
};

const ReportDetail = ({ block }) => {
  const people = Object.entries(block.related_people || {}).map(([kind, count]) => `${count} ${kind}`).join(", ");
  const engagements = Object.entries(block.parent_engagements || {}).map(([state, count]) => `${count} ${humanize(state).toLowerCase()}`).join(", ");
  return <Shell title={`Report ${block.ref}`} icon={<FileText className="w-3.5 h-3.5" />} testId="assistant-report-detail">
    <div className="font-display text-[#1B2C5A] break-words mb-2"><ReportTitle report={block} /></div>
    {block.notice && <div className="mb-2 flex gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800" data-testid="assistant-report-restricted"><ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />{block.notice}</div>}
    <div className="grid gap-x-6 sm:grid-cols-2">
      <Row label="Status" value={humanize(block.status)} />
      <Row label="Priority" value={humanize(block.priority)} />
      <Row label="Category" value={block.category} />
      <Row label="Confidentiality" value={humanize(block.confidentiality)} />
      <Row label="Submitted" value={formatDay(block.submitted_date)} />
      {block.incident_date && <Row label="Incident date" value={formatDay(block.incident_date)} />}
      <Row label="Due" value={<span className={block.overdue ? "text-red-700 font-medium" : ""}>{formatDay(block.due_date)}{block.overdue ? " · overdue" : ""}</span>} />
      <Row label="Reporter" value={[block.reporter, block.reporter_position].filter(Boolean).join(" · ")} />
      <Row label="With" value={[block.responsible, block.responsible_position].filter(Boolean).join(" · ") || "—"} />
      {people && <Row label="People involved" value={people} />}
      <Row label="Attachments" value={block.attachment_count} />
      {engagements && <Row label="Parent engagement" value={engagements} />}
    </div>
    {block.timeline?.length > 0 && <div className="mt-3"><div className="text-[11px] uppercase tracking-wide text-[#475569] mb-1">Timeline</div>
      <ol className="space-y-0.5 text-xs text-[#475569]">{block.timeline.map((item, index) => <li key={index}>{formatDay(item.date)} · {humanize(item.event)}{item.by ? ` · ${item.by}` : ""}</li>)}</ol>
    </div>}
    <div className="mt-3 flex flex-wrap items-center gap-2"><OpenReport reportId={block.report_id} />
      <span className="text-xs text-[#475569]">The full report is read in DFA.</span></div>
  </Shell>;
};

// ------------------------------------------------------------------ documents

const expired = (block) => !(Date.parse(block.expires_at) > Date.now());

const DocumentDownload = ({ block }) => {
  const [state, setState] = useState(() => (expired(block) ? "expired" : "ready"));
  const download = async () => {
    if (expired(block)) { setState("expired"); return; }
    setState("downloading");
    try {
      const { data } = await api.get(`/assistant/documents/${encodeURIComponent(block.document_id)}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a"); link.href = url; link.download = `${block.reference}.pdf`; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      setState("ready");
    } catch (error) {
      // 404 covers expired, revoked and not-yours alike; the server never says which.
      if (error?.response?.status === 404) setState("expired");
      else { toast.error(formatApiError(error)); setState("ready"); }
    }
  };
  const until = new Date(block.expires_at).toLocaleTimeString("en-NG", { hour: "2-digit", minute: "2-digit", timeZone: "Africa/Lagos" });
  return <Shell title={block.title || "Document"} icon={<FileText className="w-3.5 h-3.5" />} testId="assistant-document-download">
    <div className="flex items-center gap-2 text-xs font-medium text-red-700"><ShieldAlert className="w-4 h-4" />Confidential — management use</div>
    <div className="mt-1 text-[11px] text-[#475569]/80 font-mono break-all">{block.reference}</div>
    {state === "expired"
      ? <p className="mt-2 text-xs text-[#475569]" data-testid="assistant-document-expired">This document has expired or is no longer available. Generate a new one.</p>
      : <>
        <p className="mt-2 text-xs text-[#475569]">Generated by DFA from your authorised records. Available until {until} WAT; please don't forward it outside DFA.</p>
        <Button size="sm" className="mt-3" disabled={state === "downloading"} onClick={download}>
          {state === "downloading" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}Download PDF</Button>
      </>}
  </Shell>;
};

const ManagementQuery = ({ block, onSend, busy }) => {
  const fields = ["gender", "status", "payment_status", "outstanding_minor", "paid_minor", "performance_percent", "attendance_percent", "on_time_percent", "late_count", "absent_count", "average_delay_minutes", "unresolved_reports"];
  const value = (key, item) => key.endsWith("_minor") ? money(item) : String(item);
  return <Shell title={block.prepared ? "Prepared report · review before sharing" : block.status === "disambiguation" ? "Select a person" : "Management query"} testId="assistant-management-query">
    <p className="text-sm">{block.total} matching records{block.rows?.length ? ` · showing ${block.rows.length}` : ""}.</p>
    {block.period && <p className="text-xs mt-1">Attendance period: {block.period.start_date} to {block.period.end_date} ({block.period.timezone}).</p>}
    {block.academic_period && <p className="text-xs mt-1">Academic session: {block.academic_period.session_name} · term: {block.academic_period.term_name}</p>}
    {block.interpretations?.map((text, i) => <p className="text-xs mt-2 font-medium" key={`definition-${i}`}>{text}</p>)}
    {block.collections && <><Row label="Gross confirmed receipts" value={money(block.collections.received_minor)} /><Row label="Payment count" value={block.collections.payment_count} /></>}
    {Object.entries(block.groups || {}).map(([label, count]) => <Row key={label} label={label} value={count} />)}
    <div className="overflow-x-auto mt-3"><table className="text-xs w-full"><tbody>
      {block.rows?.map((row) => <tr key={row.person_id} className="border-t align-top"><th className="text-left py-2 pr-3 font-medium">{row.name || row.person_id}
        {block.status === "disambiguation" && <Button size="sm" disabled={busy} onClick={() => onSend?.(`Run this read query: ${JSON.stringify({ ...block.query_plan, name: null, person_id: row.person_id })}`)}>Select {row.admission_no || row.person_id}</Button>}
      </th><td className="py-2">{fields.filter((field) => row[field] !== undefined && row[field] !== null).map((field) => <div key={field}>{field.replaceAll("_", " ")}: {value(field, row[field])}</div>)}{row.invoices?.map((invoice) => <div key={invoice.invoice_id}>Invoice {invoice.invoice_id}: {invoice.status} · outstanding {money(invoice.outstanding_minor)}</div>)}</td></tr>)}
    </tbody></table></div>
    {Object.entries(block.management_sections || {}).map(([label, part]) => <div className="mt-2 text-xs" key={label}>
      <strong>{label.replaceAll("_", " ")}</strong>: {part.unavailable || `${part.total} matching records`}
      {part.totals?.outstanding_minor !== undefined && <Row label="Outstanding" value={money(part.totals.outstanding_minor)} />}
      {part.collections && <Row label="Gross receipts" value={money(part.collections.received_minor)} />}
      {[...(part.interpretations || []), ...(part.limitations || [])].map((text, i) => <p key={i}>{text}</p>)}
    </div>)}
    {block.attention_reports && <ReportList block={block.attention_reports} onQuick={() => {}} busy={true} />}
    {block.report_activity && <ReportSummary block={block.report_activity} onQuick={() => {}} busy={true} />}
    {block.limitations?.map((text, i) => <p className="text-xs mt-2 text-amber-800" key={`limitation-${i}`}>{text}</p>)}
    {block.prepared && <p className="text-xs mt-2 font-medium">Prepared for your review. Nothing has been sent or submitted.</p>}
    {block.next_offset != null && <Button size="sm" disabled={busy} onClick={() => onSend?.(`Run this read query: ${JSON.stringify({ ...block.query_plan, offset: block.next_offset })}`)}>Next page</Button>}
  </Shell>;
};

export default function AssistantBlock({ block, onQuick, onSend, onConfirm, onCancel, busy, resolvedActions = {} }) {
  switch (block.type) {
    case "prepared_report": return <Shell title="Prepared report · review" testId="assistant-prepared-report">
      <p className="text-sm">For {block.recipient?.name} · {block.recipient?.position} · {block.recipient?.unit}</p>
      <ReportSummary block={block.summary} onQuick={onQuick} busy={busy} />
      <p className="text-xs mt-2">{block.own_only ? "Your recorded report activity." : "Authorized report activity."} Nothing has been sent or submitted.</p>
    </Shell>;
    case "management_query": return <ManagementQuery block={block} onSend={onSend} busy={busy} />;
    case "balance_summary": return <BalanceSummary block={block} onQuick={onQuick} busy={busy} />;
    case "product_options": return <ProductOptions block={block} onQuick={onQuick} busy={busy} />;
    case "payment_confirmation": return <PaymentConfirmation block={block} onConfirm={onConfirm} onCancel={onCancel} busy={busy} resolved={resolvedActions[block.action_id]} />;
    case "payment_link": return <PaymentLink block={block} />;
    case "payment_status": return block.checkouts?.length ? <PaymentStatus block={block} onQuick={onQuick} busy={busy} /> : null;
    case "payment_history": return block.payments?.length ? <PaymentHistory block={block} onQuick={onQuick} busy={busy} /> : null;
    case "receipt": return <Receipt block={block} />;
    case "student_selector": return <StudentSelector block={block} onSend={onSend} busy={busy} />;
    case "capabilities": return <Capabilities block={block} />;
    case "report_summary": return <ReportSummary block={block} onQuick={onQuick} busy={busy} />;
    case "report_list": return <ReportList block={block} onQuick={onQuick} busy={busy} />;
    case "report_detail": return <ReportDetail block={block} />;
    case "document_download": return <DocumentDownload block={block} />;
    case "notice": return <p className="mt-2 text-xs text-[#475569]">{block.text}</p>;
    default: return null;
  }
}
